import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section,
} from "../components/Primitives";
import { getConfig, getConsultants } from "../lib/api";
import { useSEO } from "../lib/seo";
import { IMAGES } from "../content/images";
import { COMPANY } from "../content/nav";

function Page({ crumb, title, eyebrow, lede, children, seo }) {
  useSEO(seo);
  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }, { label: crumb }]} />
      </Container>
      <Section className="border-b border-rule pb-10">
        <Container>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <H1 className="mt-5 max-w-3xl">{title}</H1>
          {lede && <Lede className="mt-6">{lede}</Lede>}
        </Container>
      </Section>
      {children}
    </>
  );
}

export function About() {
  return (
    <Page
      crumb="About"
      eyebrow="Shreem · श्रीं"
      title="A mantra is something you practise, not something you purchase"
      lede="That is roughly how we think about everything we sell."
      seo={{
        title: "About Shreem Mantra Energy — Proof You Can Check",
        description:
          "Why we are named after a seed syllable, why we publish our margin band, and why the constraints on this website are the product rather than a stylistic preference.",
        canonical: "/about",
      }}
    >
      <Section className="border-b border-rule">
        <Container className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="measure space-y-5 text-base text-dim">
            <p>
              <strong className="text-ink">The name.</strong> Shreem (श्री) is the bīja — the seed
              syllable — associated with Lakshmi: abundance, sustenance, and the quiet kind of prosperity
              that holds. It is a sound, not a promise. We chose it because a mantra is something you
              practise, not something you purchase, and that is roughly how we think about everything we
              sell.
            </p>
            <p>
              <strong className="text-ink">What we do.</strong> We sell certified natural gemstones,
              rudraksha beads, malas and yantras, and we sell paid one-to-one Vedic astrology and
              numerology consultations at a flat fee. Those two things are invoiced separately, with
              separate tax codes, and are never bundled into a single price — which is why a consultation
              fee is credited against a later purchase rather than folded into a package.
            </p>
            <p>
              <strong className="text-ink">The one thing that matters.</strong> The entire brand is built
              on verifiable proof. Where there is a choice between something impressive and something
              checkable, we choose checkable. Every report links to the laboratory's own domain. Every
              rudraksha above ₹3,000 publishes its X-ray. Every product page publishes a margin band.
              None of that is generosity; it is the only defence against a category that has earned its
              reputation.
            </p>
            <p>
              <strong className="text-ink">What we will not do.</strong> No countdown timers. No
              struck-through inflated MRP. No pre-ticked consent. No wallet or stored balance. No
              per-minute billing. No claim that anything here cures, treats, prevents or mitigates any
              disease. No prediction of a death, and none about the health of a member of your family.
              Several of those are named dark patterns under Indian consumer-protection guidelines and
              one of them is a criminal-law exposure — but we would decline them anyway.
            </p>
            <p>
              <strong className="text-ink">Who is behind it.</strong> {COMPANY.legal_name}, registered
              in India. Every escalation ends with a named person — write to {COMPANY.email} and it
              will reach them within one working day.
            </p>
          </div>
          <div>
            <img src={IMAGES.sourcing} alt="An artisan sorting loose coloured stones on a paper parcel in a Jaipur workshop" className="w-full border border-rule object-cover" />
            <p className="ui mt-3 text-xs italic text-dim">
              Our sorting bench. Real workspace, natural light, no stock imagery of serene women
              meditating.
            </p>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <H2>Where to go from here</H2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["/promises", "The Twelve Promises", "Twelve commitments, six of them guarantees, each with a mechanism for holding us to it."],
              ["/about/scoreboard", "Our scoreboard", "Refund rate, response times and the percentage of consultations ending with no recommendation."],
              ["/about/how-we-source", "How we source", "Jaipur, Nepal and Java — named suppliers, named processes."],
              ["/about/verification", "How to verify anything", "The exact steps for checking a report, an X-ray or us."],
              ["/about/labs-we-refuse", "Labs we refuse", "Named, with the documented substitution cases."],
              ["/about/energisation", "What energisation is", "And, more usefully, what it isn't."],
            ].map(([to, title, note]) => (
              <Link key={to} to={to} className="card-s p-6 transition-colors hover:border-gold">
                <H3>{title}</H3>
                <p className="ui mt-2 text-sm text-dim">{note}</p>
              </Link>
            ))}
          </div>
        </Container>
      </Section>
    </Page>
  );
}

export function Consultants() {
  const { data } = useQuery({ queryKey: ["consultants"], queryFn: getConsultants });
  return (
    <Page
      crumb="Consultants"
      eyebrow="No pseudonyms, no ratings theatre"
      title="Our consultants"
      lede="Real names, real training lineage, years practising, languages and specialisms. No celebrity badges, no star ratings, and a commission rate of zero that is a hardcoded constant rather than a setting."
      seo={{
        title: "Our Consultants — Named, Trained, Zero Commission",
        description:
          "Every consultant's real name, training lineage, years practising, languages and specialisms. Commission rate zero, written into their contract.",
        canonical: "/about/consultants",
      }}
    >
      <Section>
        <Container className="space-y-8">
          {(data?.items || []).map((c) => (
            <article key={c.slug} className="card-s grid gap-6 p-6 sm:grid-cols-[180px_1fr] sm:p-8" data-testid={`consultant-${c.slug}`}>
              <img src={c.photo} alt={c.display_name} className="w-full object-cover" />
              <div>
                <H2>{c.display_name}</H2>
                <p className="num mt-1 text-sm text-dim">
                  {c.years_practising} years practising · {c.languages.join(", ")}
                </p>
                <p className="ui mt-4 text-sm leading-relaxed text-dim">{c.bio}</p>
                <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="ui text-xs text-dim">Training lineage</dt>
                    <dd className="ui mt-1 text-ink">{c.training_lineage}</dd>
                  </div>
                  <div>
                    <dt className="ui text-xs text-dim">Specialisms</dt>
                    <dd className="ui mt-1 text-ink">{c.specialisms.join(" · ")}</dd>
                  </div>
                  <div>
                    <dt className="ui text-xs text-dim">Registrations</dt>
                    <dd className="ui mt-1 text-ink">
                      {c.registrations.length
                        ? c.registrations.join(", ")
                        : "None claimed. They are not registered medical practitioners, psychiatrists or RCI-registered clinical psychologists, and we do not imply otherwise."}
                    </dd>
                  </div>
                  <div>
                    <dt className="ui text-xs text-dim">Commission rate</dt>
                    <dd className="num mt-1 text-verify">0 — contractual, not aspirational</dd>
                  </div>
                </dl>
                <p className="ui mt-4 text-xs text-dim">
                  Holds {c.sessions_per_week} sessions a week. Next available slots are live on the
                  booking calendar — never a hardcoded "2 left" banner.
                </p>
              </div>
            </article>
          ))}
        </Container>
      </Section>
    </Page>
  );
}

export function HowWeSource() {
  return (
    <Page
      crumb="How we source"
      eyebrow="Jaipur · Nepal · Java"
      title="How we source"
      lede="Three supply chains, three sets of problems, and the specific things we do about each."
      seo={{
        title: "How We Source — Jaipur, Nepal and Java",
        description:
          "Named suppliers, the photography and X-ray standard every SKU must meet before it can be published, and the lighting standard we publish so nobody can grade a stone by grading the photograph.",
        canonical: "/about/how-we-source",
      }}
    >
      <Section>
        <Container className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="measure space-y-5 text-base text-dim">
            <p>
              <strong className="text-ink">Jaipur — coloured stones.</strong> Cut and sorted at our
              partner atelier, where we buy loose parcels and reject at the bench rather than at the
              photograph. Every stone leaves with a laboratory report before it is listed, not after
              somebody asks for one. Where we cannot get a report at a price the stone justifies, we list
              it honestly as self-described or we do not list it.
            </p>
            <p>
              <strong className="text-ink">Nepal — rudraksha.</strong> Sourced through a family we've
              worked with for years, at the Nepal foothills. Every bead above ₹3,000 is X-rayed at an
              accredited facility before the SKU can go
              live — the image upload is a hard requirement in our admin, not a checklist item somebody
              can forget. Beads that show carving, joining or a chamber count that does not match the
              mukhi lines are returned, and we keep the radiographs.
            </p>
            <p>
              <strong className="text-ink">Java — rudraksha.</strong> Java beads are smaller, denser and
              considerably cheaper than the Nepal equivalent, and they are not inferior. We say so on
              every page, because the easiest sale in this category is a Nepal bead to somebody who was
              never told the Java one would do.
            </p>
            <p>
              <strong className="text-ink">The photography standard.</strong> Every item is photographed
              out of its packet on a neutral grey card with a millimetre scale in at least one frame, four
              angles minimum, plus a rotation video above ₹10,000. No colour grading that changes a
              stone's apparent hue. The lighting standard is published on our verification page so you can
              compare our photographs against anybody else's.
            </p>
          </div>
          <div className="space-y-4">
            <img src={IMAGES.sourcing} alt="Sorting loose stones at the bench in Jaipur" className="w-full border border-rule" />
            <img src={IMAGES.xray} alt="An X-ray radiograph of a rudraksha bead with the chambers visible" className="w-full border border-rule" />
          </div>
        </Container>
      </Section>
    </Page>
  );
}

export function Verification() {
  const { data } = useQuery({ queryKey: ["config"], queryFn: getConfig });
  return (
    <Page
      crumb="Verification"
      eyebrow="Do this before you trust us"
      title="How to verify anything you buy from us"
      lede="Four checks, none of which involve taking our word for anything."
      seo={{
        title: "How to Verify Anything You Buy From Us",
        description:
          "Four checks: the laboratory report on the laboratory's own domain, the X-ray chamber count, independent re-testing at our expense, and the published lighting standard for our photography.",
        canonical: "/about/verification",
      }}
    >
      <Section className="border-b border-rule">
        <Container>
          <ol className="divide-y divide-rule">
            {[
              ["Check the report on the laboratory's own site", "Every product page carries the report number as a link. It opens the laboratory's domain in a new tab — gia.edu, gtljaipur.info, igi.org and so on. It is never a page on our domain, and that is enforced in code: if a verification URL pointed at shreemmantraenergy.com, the record would refuse to save."],
              ["Count the chambers on the X-ray", "Every rudraksha above ₹3,000 publishes its own radiograph as a gallery slide. A genuine bead has one seed chamber per mukhi line. Grooves carved on the outside cannot fake what is inside, which is precisely why we publish the inside."],
              ["Re-test it, at our expense", "Take anything you buy to any accredited laboratory within 30 days. If the species, treatment status, specific gravity or refractive index differ materially from the report we supplied, we refund 100% and reimburse your laboratory fee on production of the invoice."],
              ["Compare our photograph against our published lighting standard", "Diffuse overhead light at 5500K, neutral grey card, no polarising filter, no colour grading that alters apparent hue, millimetre scale in frame. If a photograph on this site looks better than the stone in your hand, that is a complaint we want."],
            ].map(([h, p], i) => (
              <li key={h} className="grid gap-4 py-8 lg:grid-cols-[60px_1fr]">
                <span className="num text-3xl text-gold/75">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <H3>{h}</H3>
                  <p className="measure ui mt-2 text-sm leading-relaxed text-dim">{p}</p>
                </div>
              </li>
            ))}
          </ol>
        </Container>
      </Section>

      <Section>
        <Container>
          <H2>Laboratories whose reports we accept</H2>
          <div className="mt-8 grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-3">
            {(data?.labs || []).map((l) => (
              <a
                key={l.code}
                href={l.verify_url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-surface p-5 transition-colors hover:bg-wash"
              >
                <p className="ui text-sm font-medium text-ink">{l.code}</p>
                <p className="ui mt-1 text-xs text-dim">{l.name}</p>
                <p className="num mt-2 text-xs text-gold">{l.domain} →</p>
              </a>
            ))}
          </div>
          <p className="ui mt-6 text-sm text-dim">
            <Link to="/about/labs-we-refuse" className="link-u">And the ones we refuse →</Link>
          </p>
        </Container>
      </Section>
    </Page>
  );
}

export function LabsWeRefuse() {
  const { data } = useQuery({ queryKey: ["config"], queryFn: getConfig });
  return (
    <Page
      crumb="Labs we refuse"
      eyebrow="This page names names"
      title="Laboratories we will not accept"
      lede="We will not sell a stone on the strength of a report from a laboratory we do not trust, and we will not accept one from a customer as proof either. A page that says 'we only use trusted labs' without saying which ones is worth nothing."
      seo={{
        title: "Laboratories We Will Not Accept Certificates From",
        description:
          "The documented substitution cases, the general categories of certificate we refuse, and why 'government approved gemstone certificate' describes a scheme that does not exist in India.",
        canonical: "/about/labs-we-refuse",
      }}
    >
      <Section className="border-b border-rule">
        <Container>
          <H2>The documented substitution case</H2>
          <p className="measure ui mt-4 text-sm leading-relaxed text-dim">
            The German laboratory EPI published an investigation into certificates issued by AGI Labs /
            IGL. Stones were submitted and came back described as something they demonstrably were not:
          </p>
          <table className="mt-6 w-full max-w-2xl text-sm" data-testid="substitution-table">
            <thead>
              <tr className="border-b border-rule">
                <th scope="col" className="ui py-2 text-left text-xs uppercase tracking-wider text-dim">What was submitted</th>
                <th scope="col" className="ui py-2 text-left text-xs uppercase tracking-wider text-dim">What it was certified as</th>
              </tr>
            </thead>
            <tbody>
              {(data?.labs_we_refuse_table || []).map((r) => (
                <tr key={r.claim} className="border-b border-rule">
                  <td className="ui py-3 pr-4 text-dim">{r.claim}</td>
                  <td className="ui py-3 text-ink">{r.certified_as}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <blockquote className="measure mt-6 border-l-2 border-l-alert pl-5 text-sm italic text-dim">
            "We could not imagine that these false certificates were written accidental or mistaken. We
            assume criminal intentions." — EPI's published conclusion
          </blockquote>
        </Container>
      </Section>

      <Section className="border-b border-rule bg-surface">
        <Container>
          <H2>The general categories we refuse</H2>
          <ul className="ui mt-6 space-y-3 text-sm text-dim">
            <li>· In-house laboratories — any laboratory owned by, or housed within, the seller.</li>
            <li>· Reports with no treatment line at all. Silence on treatment is the tell.</li>
            <li>· Laminated appraisal cards carrying a price and no lab number or signatory.</li>
            <li>· Anything described as a "government approved gemstone certificate".</li>
            <li>· A QR code that opens the seller's website rather than the laboratory's.</li>
          </ul>
        </Container>
      </Section>

      <Section>
        <Container className="measure">
          <H2>A note on that last phrase</H2>
          <p className="ui mt-4 text-sm leading-relaxed text-dim">
            You will see "Government Certified Gemstone" and "Certified by Govt Labs" used by well-known
            sellers in India. There is no government gemstone certification scheme. The Gem Testing
            Laboratory in Jaipur operates under the Gem &amp; Jewellery Export Promotion Council, which is
            sponsored by the Ministry of Commerce &amp; Industry — that is a real and respectable
            affiliation, and it is not the same thing as a government certificate. We use GTL. We do not
            call it a government certificate, and we would rather explain the difference than borrow the
            authority.
          </p>
          <p className="mt-6 text-[15px] leading-[1.75] text-dim">
            If you believe we have wrongly named a laboratory here, write to {COMPANY.email} with
            your evidence and we will publish a correction.
          </p>
        </Container>
      </Section>
    </Page>
  );
}

export function Energisation() {
  return (
    <Page
      crumb="Energisation"
      eyebrow="Included, never an upgrade"
      title="What energisation is, and what it isn't"
      lede="A traditional ritual practice, performed by a named priest at a named place on a stated date, with a video attached to your order. No result of any kind is claimed or guaranteed."
      seo={{
        title: "What Energisation Is, and What It Isn't",
        description:
          "Energisation is included at no charge, with the priest named, the place named, the date stated and a video attached. Charging separately for something described as essential is drip pricing.",
        canonical: "/about/energisation",
      }}
    >
      <Section>
        <Container className="measure space-y-5 text-base text-dim">
          <p>
            <strong className="text-ink">What it is.</strong> A traditional ritual — the recitation of the
            mantra classically associated with the relevant graha over the item, a stated number of times,
            by a priest we name, at a place we name, on a date we state. You receive a short video with the
            date visible, attached to your order.
          </p>
          <p>
            <strong className="text-ink">What it isn't.</strong> It is not a process that changes anything
            measurable about the object. Nothing about it is a claim of efficacy, and no outcome is
            promised. If you find that framing disappointing, we would rather disappoint you here than
            charge you for a promise we cannot keep.
          </p>
          <p>
            <strong className="text-ink">Why it's free.</strong> One large competitor runs an energisation
            ladder from free up to a paid live-streamed puja. Charging separately for something we describe
            as essential is drip pricing, which is a named dark pattern under Indian consumer-protection
            guidelines. So it's included, the proof is included, and you may also decline it at no change
            in price.
          </p>
          <p>
            <strong className="text-ink">If you'd rather not.</strong> There is an unticked box on every
            product page for exactly that. Plenty of people want the stone and not the rite, and that is a
            perfectly coherent position.
          </p>
        </Container>
      </Section>
    </Page>
  );
}
